import React from "react";
import { useTranslation } from "react-i18next";

import { prependServiceBaseUrl } from "../../../../commons/libs/client-utils";
import envelope from "../../../../commons/libs/externals/envelope";
import { ModalProps, PrintMailModalContentProps } from "../../../../commons/types/modal";
import { PrintType } from "../../../../commons/types/print-mail";
import * as icons from "../../../../resources/icons";
import mailImage from "../../../../resources/images/email.png";
import printerImage from "../../../../resources/images/printer.png";
import { ROUTES } from "../../../commons/routes";
import Button from "../../components/Button/Button";
import FlexFooterLayout from "../../components/FlexFooterLayout/FlexFooterLayout";
import FlexLayout from "../../components/FlexLayout/FlexLayout";
import Headline from "../../components/Headline/Headline";
import Icon from "../../components/Icon/Icon";
import IconButton from "../../components/IconButton/IconButton";
import IconLabel from "../../components/IconLabel/IconLabel";
import Image from "../../components/Image/Image";
import Modal from "../../components/Modal/Modal";
import Paragraph from "../../components/Paragraph/Paragraph";
import SplitContentLayout from "../../components/SplitContentLayout/SplitContentLayout";
import { useAppHistory } from "../../libs/hooks/use-app-routing";
import { createPreviewUrl } from "../../libs/print";
import { useGetPdfMetaDataFromUrl } from "../../libs/queries/use-pdf";
import { useSettings } from "../../libs/queries/use-settings";
import { useCommonAppDispatcher } from "../../store/hooks";
import DelayedLoadingIndicator from "../partials/DelayedLoadingIndicator";

interface Props extends ModalProps<PrintMailModalContentProps> {
  nodeRef?: React.Ref<HTMLElement>;
  isOffline: boolean;
  isUsbPrinterConnected: boolean;
}

const PrintMailSwitchModalPartial = ({ close, data, isOffline, isUsbPrinterConnected, nodeRef }: Props) => {
  const isMobile = envelope.isMobileBuild;

  const [dispatch, actions] = useCommonAppDispatcher();
  const history = useAppHistory();
  const { t } = useTranslation(["commons"]);

  const settings = useSettings();

  const printTypeTitle = {
    [PrintType.ProductDataSheet as string]: t("commons:printMailSwitchModalPartialApp.shareDataSheetTitle"),
    [PrintType.Default as string]: t("commons:printMailSwitchModalPartialApp.shareTitle")
  };

  const isPrintTypeProductDataSheet = data.type === PrintType.ProductDataSheet;

  const previewUrl = React.useMemo(() => createPreviewUrl(data), [data]);
  const previewPdf = useGetPdfMetaDataFromUrl({ url: previewUrl });
  const pdfUrl = previewPdf?.data?.url ? prependServiceBaseUrl(previewPdf.data.url) : "";

  const renderKioskPrintFooter = React.useCallback(() => {
    return !isUsbPrinterConnected && !settings.hasMailToPrintSettings ? (
      <Button
        variant="accent"
        kind="solid"
        icon={<Icon source={icons.IconSmallArrowRight} />}
        iconRight
        onClick={() => {
          close();
          history.push(ROUTES.SETTINGS.PRINTER_INDEX);
        }}
      >
        {t("commons:printMailSwitchModalPartialApp.setupPrinterButton")}
      </Button>
    ) : (
      <Button
        variant="accent"
        kind="solid"
        icon={<Icon source={icons.IconSmallArrowRight} />}
        iconRight
        disabled={previewPdf.isLoading}
        onClick={() => dispatch(actions.modal.showModal("print", { data }))}
      >
        {t("commons:printMailSwitchModalPartialApp.toPrintPreviewButton")}
      </Button>
    );
  }, [
    data,
    isUsbPrinterConnected,
    previewPdf.isLoading,
    settings.hasMailToPrintSettings,
    t,
    close,
    history,
    dispatch,
    actions.modal
  ]);

  const renderMobilePrintFooter = React.useCallback(() => {
    return (
      <FlexLayout direction="column" gap="base">
        <Button
          classNames={["u-space-top-xs"]}
          icon={<Icon source={icons.IconSmallArrowRight} />}
          disabled={previewPdf.isLoading}
          iconRight
          onClick={() => window.open(pdfUrl)}
        >
          {t("commons:printMailSwitchModalPartialApp.openInNewTabButton")}
        </Button>
        {settings.hasMailToPrintSettings && (
          <Button
            variant="accent"
            kind="solid"
            disabled={previewPdf.isLoading || isOffline}
            icon={<Icon source={icons.IconSmallArrowRight} />}
            iconRight
            onClick={() => dispatch(actions.modal.showModal("print", { data }))}
          >
            {t("commons:printMailSwitchModalPartialApp.toPrintPreviewButton")}
          </Button>
        )}
      </FlexLayout>
    );
  }, [data, isOffline, pdfUrl, previewPdf.isLoading, t, settings.hasMailToPrintSettings, dispatch, actions.modal]);

  return (
    <Modal
      nodeRef={nodeRef}
      width="xl"
      closeButton={<IconButton onClick={() => close()} icon={<Icon source={icons.IconSmallCross} />} />}
    >
      <Headline kind="m">
        <FlexLayout alignItems="center" gap="base">
          {printTypeTitle[data.type]}
          {previewPdf.isLoading && <DelayedLoadingIndicator size="s" />}
        </FlexLayout>
      </Headline>
      <SplitContentLayout size="m">
        <FlexFooterLayout footer={isMobile ? renderMobilePrintFooter() : renderKioskPrintFooter()}>
          <Image position="center" classNames={["u-space-xl"]} src={printerImage} />
          <Headline kind="sub">{t("commons:printMailSwitchModalPartialApp.analogHeadline")}</Headline>
          <Headline classNames={["u-space-base"]}>
            {t("commons:printMailSwitchModalPartialApp.viaPrinterHeadline")}
          </Headline>
          <Paragraph kind="support">
            {isPrintTypeProductDataSheet
              ? t("commons:printMailSwitchModalPartialApp.printDataSheetInShop")
              : t("commons:printMailSwitchModalPartialApp.printInShop")}
          </Paragraph>
        </FlexFooterLayout>

        <FlexFooterLayout
          footer={
            <Button
              variant="accent"
              kind="solid"
              icon={<Icon source={icons.IconSmallArrowRight} />}
              iconRight
              onClick={() => dispatch(actions.modal.showModal("mail", { data }))}
              disabled={previewPdf.isLoading || isOffline}
            >
              {t("commons:printMailSwitchModalPartialApp.enterEmailAddressButton")}
            </Button>
          }
        >
          <Image position="center" classNames={["u-space-xl"]} src={mailImage} />
          <Headline kind="sub">Digital</Headline>
          <Headline classNames={["u-space-base"]}>Via E-Mail</Headline>
          <Paragraph kind="support">
            {isPrintTypeProductDataSheet
              ? t("commons:printMailSwitchModalPartialApp.sendDataSheetToEmail")
              : t("commons:printMailSwitchModalPartialApp.sendToEmail")}
          </Paragraph>
        </FlexFooterLayout>
      </SplitContentLayout>

      <SplitContentLayout size="m" separator={false}>
        <div id="error-messages-for-printing-function">
          {!isMobile && settings.isPrintingActive && !settings.hasMailToPrintSettings && !isUsbPrinterConnected && (
            <IconLabel kind="negative" icon={<Icon source={icons.IconSmallAttention} />}>
              {t("commons:printMailSwitchModalPartialApp.usbPrintingIsActiveButNoConnectedPrinter")}
            </IconLabel>
          )}

          {!isMobile && settings.isPrintingActive && settings.hasMailToPrintSettings && isOffline && (
            <IconLabel kind="negative" icon={<Icon source={icons.IconSmallAttention} />}>
              {t("commons:printMailSwitchModalPartialApp.mailToPrintIsActiveButOffline")}
            </IconLabel>
          )}
        </div>

        <div id="error-messages-for-mail-function">
          {!isMobile && settings.isMailActive && settings.hasMailSettings && isOffline && (
            <IconLabel kind="negative" icon={<Icon source={icons.IconSmallAttention} />}>
              {t("commons:printMailSwitchModalPartialApp.mailIsActiveButOffline")}
            </IconLabel>
          )}
        </div>
      </SplitContentLayout>
    </Modal>
  );
};

export default PrintMailSwitchModalPartial;
